import {
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { CommentIcon } from "components/_Icons/CommentIcon";
import DirectIcon from "components/_Icons/DirectIcon";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import IPhoneChat from "components/iPhone/iPhoneChat";
import PostWithComments from "components/iPhone/iPhonePostComments";
import { FaArrowLeft, FaPlus, FaTrash, FaTrashAlt } from "react-icons/fa";
import withRouter from "hoc/withRouter";
import { withRemoteDataAndSpinner } from "hoc/withRemoteDataAndSpinner";
import channelConnections from "services/admin/channelConnections";
import { useLoadAll } from "hooks/useLoadAll";
import { automationsService } from "services/admin/automationsService";
import _ from "lodash";
import { NodeType } from "./AutomationConstructor/canvas-utils";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AIIcon } from "components/_Icons/AIIcon";
import { Alert } from "@mui/material";
import ChatbotTestDialog from "components/Onboarding/ChatbotTestDialog";
import { MdClose } from "react-icons/md";

const STEP_LABELS = ["Запуск", "Сообщение", "Дополнительно"];

interface AutomationCreateKeywordProps {
  data: {
    accounts: any[];
    automation: any;
  };
  router: any;
}

/**
 * Чат-бот на кодовое слово
 * Структура страницы:
 * Слева: Форма для создания автоматизации
 * Справа: Превью автоматизации ввиде дизайна iPhone
 *
 * Форма для создания автоматизации:
 * - Название автоматизации
 * - Выбирете аккаунт из списка аккаунтов
 * - Условия запуска автоматизации
 *
 *
 * Условия запуска автоматизации:
 * - Сообщение в Директ
 * - Комментарий к посту
 *
 * @returns
 */
const AutomationCreateKeyword = ({
  data: { accounts, automation },
  router,
}: AutomationCreateKeywordProps) => {
  console.log("accounts=>", {
    accounts,
    channelConnectionID: router.queryParams.get("channelConnectionID") || "",
  });
  const navigate = useNavigate();

  // Из «Быстрого старта» (после входа через Instagram): всё заполнено — остаётся проверить и создать
  const isOnboarding = router.queryParams.get("onboarding") === "1";
  const queryChannelID =
    Number(router.queryParams.get("channelConnectionID")) || "";
  // Единственный аккаунт выбираем сразу
  const defaultChannelID =
    queryChannelID || (accounts.length === 1 ? accounts[0].id : "");
  // Окно «Как протестировать чат-бота?» после создания
  const [testDialog, setTestDialog] = useState<{
    username?: string;
    keywords: string[];
    direct: boolean;
    comment: boolean;
  } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      name: automation
        ? automation.name
        : router.queryParams.get("name") ||
          (isOnboarding ? "Ответ на кодовое слово «цена»" : ""),
      channelConnectionID: automation
        ? automation.channelConnectionID
        : defaultChannelID,
      // Источник триггера
      triggerSource: automation
        ? automation.triggerSource
        : {
            DIRECT: true,
            COMMENT: false,
          },
      triggerType: automation
        ? automation.triggerType
        : isOnboarding
          ? "KEYWORD"
          : "ANY",
      intention: automation ? automation.intention : "",
      preventDuplicateLaunch: automation
        ? automation.preventDuplicateLaunch
        : false,
      // Проверить подписку на аккаунт
      // В онбординге выключено: иначе тестовый аккаунт без подписки получит просьбу подписаться вместо ответа
      checkSubscribe: automation
        ? (automation.checkSubscribe ?? true)
        : !isOnboarding,
      // Кодовые слова хранятся в automation.data.keywords (бэкенд собирает из них триггеры)
      keywords: automation
        ? automation.data?.keywords || automation.keywords || []
        : isOnboarding
          ? ["цена"]
          : [],
      // Точное совпадение
      exactMatch: automation ? automation.exactMatch : true,
      // Настройка сообщений
      // У сохранённой автоматизации nodes может не быть — тогда показываем шаблон
      nodes: automation?.nodes?.length
        ? automation.nodes
        : isOnboarding
          ? [
              {
                type: NodeType.MESSAGE,
                // Текст безопасен «как есть»: его увидят реальные клиенты, если не поменять
                text: "Здравствуйте! 👋 Спасибо, что написали. Менеджер скоро пришлёт вам актуальные цены. А пока расскажите, что вас интересует?",
                image: "",
                buttons: [],
              },
            ]
          : [
              {
                type: NodeType.MESSAGE,
                text: "Ваше приветственное сообщение",
                image: "",
                buttons: [
                  {
                    text: "Текст кнопки",
                    url: "",
                  },
                ],
              },
            ],
      edges: automation?.edges || [],
      // Автоответы на комментарии
      replyCommentsEnabled: automation
        ? automation.replyCommentsEnabled
        : false,
      replyComments: automation?.replyComments?.length
        ? automation.replyComments
        : [
            {
              text: "",
            },
          ],
      aiEnabled: automation ? automation.aiEnabled : false,
    },
  });

  const [steps, setSteps] = useState<number>(1);
  const [tab, setTab] = useState<string>("messages");

  useEffect(() => {
    // Disable scroll on the page
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [steps]);

  const account = useMemo(() => {
    return accounts.find(
      (account) => account.id === watch("channelConnectionID"),
    );
  }, [accounts, watch("channelConnectionID")]);

  useEffect(() => {
    console.log("edges=>", getValues("edges"));

    setValue(
      "edges",
      (getValues("nodes") || []).map((node: any, index: number) => {
        let fromButtonID = null;
        if (node.type === NodeType.MESSAGE && node.buttons?.length > 0) {
          fromButtonID = 0;
        }

        let toNodeID = null;
        if (index < getValues("nodes").length - 1) {
          toNodeID = index + 1;
        }

        return {
          fromNodeID: index,
          fromButtonID: fromButtonID,
          toNodeID: toNodeID,
        };
      }),
    );
  }, [getValues("nodes")]);

  const onSubmit = async (formInput: any) => {
    console.log("Создаем или обновляем автоматизацию: ", {
      formInput,
      steps,
    });
    try {
      if (automation) {
        await automationsService.updateAutomation(
          Number(automation.id),
          formInput,
        );
        toast.success("Автоматизация успешно обновлена");
      } else {
        await automationsService.createAutomation({
          ...formInput,
          templateID: "keyword",
        });
        toast.success("Чат-бот создан и уже работает");
        // Сразу показываем, как проверить бота с другого аккаунта
        setTestDialog({
          username: account?.externalAccountUserName,
          keywords:
            formInput.triggerType === "KEYWORD" ? formInput.keywords : [],
          direct: !!formInput.triggerSource?.DIRECT,
          comment: !!formInput.triggerSource?.COMMENT,
        });
        return;
      }
      setTimeout(() => {
        navigate("/admin/automations");
      }, 500);
    } catch (error) {
      console.error(error);
      // Понятную ошибку с сервера (например, кодовое слово занято) уже показал интерцептор axios
      if (!(error as any)?.response?.data?.error?.message) {
        toast.error("Ошибка при сохранении автоматизации");
      }
    }
  };

  const closePath = isOnboarding ? "/admin/stats" : "/admin/automations";
  const goBack = () => {
    if (steps === 1) {
      navigate(closePath);
      return;
    }
    setSteps(steps - 1);
  };

  // Полноэкранный конструктор без меню и шапки админки: сверху шаги, слева форма, справа превью
  return (
    <div className="automation-create-keyword min-h-screen lg:h-screen w-full bg-white lg:overflow-hidden">
      <header className="h-16 border-b bg-white flex items-center gap-2 sm:gap-3 px-3 sm:px-6 sticky top-0 z-20">
        <IconButton onClick={goBack} size="small" aria-label="Назад">
          <FaArrowLeft size={18} />
        </IconButton>
        <img
          src="/media/images/ubee-logo.png"
          alt="Ubee AI"
          className="hidden sm:block w-8 h-8 rounded-lg object-contain"
        />
        <div className="font-bold text-base sm:text-lg truncate">
          {automation
            ? "Чат-бот на кодовое слово"
            : "Новый чат-бот на кодовое слово"}
        </div>
        <ol className="hidden md:flex items-center gap-2 mx-auto">
          {STEP_LABELS.map((label, index) => {
            const step = index + 1;
            const isActive = step === steps;
            const isDone = step < steps;
            return (
              <li key={label} className="flex items-center gap-2">
                {index > 0 && <span className="w-8 h-px bg-gray-200" />}
                <button
                  type="button"
                  disabled={!isValid && step > steps}
                  onClick={() => setSteps(step)}
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? "bg-[#f3ecfa] text-[#6b2fa3] font-semibold"
                      : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                      isActive
                        ? "bg-[#6b2fa3] text-white"
                        : isDone
                          ? "bg-green-500 text-white"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {isDone ? "✓" : step}
                  </span>
                  {label}
                </button>
              </li>
            );
          })}
        </ol>
        <div className="ml-auto md:ml-0 text-sm text-gray-500 whitespace-nowrap md:hidden">
          Шаг {steps} из 3
        </div>
        <IconButton
          onClick={() => navigate(closePath)}
          size="small"
          aria-label="Закрыть"
        >
          <MdClose size={22} />
        </IconButton>
      </header>

      <div className="lg:h-[calc(100vh-64px)] flex">
        {/* Форма */}
        <div className="w-full lg:w-[560px] xl:w-[620px] shrink-0 lg:border-r lg:h-full">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="lg:h-full flex flex-col"
          >
            <div className="flex-grow lg:overflow-y-auto px-4 sm:px-8 py-6">
              {isOnboarding && steps === 1 && (
                <Alert severity="success" className="mb-4">
                  Instagram подключён! Мы уже заполнили чат-бота: на кодовое
                  слово <b>«цена»</b> в Директ он ответит приветствием.
                  Проверьте настройки и текст сообщения или сразу нажмите
                  «Создать чат-бота».
                </Alert>
              )}
              {steps === 1 && (
                <section id="step-1">
                  <div className="mb-4">
                    <div className="text-lg font-bold mb-2">
                      Выберите аккаунт
                    </div>
                    <Controller
                      control={control}
                      name="channelConnectionID"
                      render={({ field }) => (
                        <Select fullWidth {...field}>
                          {accounts.map((account) => (
                            <MenuItem key={account.id} value={account.id}>
                              {account.externalAccountUserName}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-lg font-bold">
                        Проверить подписку
                      </div>
                      <p className="text-sm text-gray-500">
                        Чат-бот проверит подписку на аккаунт
                      </p>
                    </div>
                    <Switch {...register("checkSubscribe")} />
                  </div>
                  <div className="mb-4">
                    <h4 className="">Условия запуска</h4>
                    <p className="text-sm text-gray-500">
                      Какое действие запустит чат-бота?
                    </p>
                    <div className="flex flex-col gap-2 mb-4">
                      <Controller
                        control={control}
                        name={`triggerSource.DIRECT`}
                        render={({ field }) => (
                          <div
                            className="flex items-center gap-2 border border-gray-200 rounded-md p-2 cursor-pointer"
                            style={{
                              borderColor: field.value
                                ? "var(--bs-primary)"
                                : "",
                            }}
                          >
                            <div className="pl-2">
                              <DirectIcon size={20} />
                            </div>
                            <label
                              className="text-lg font-bold flex-1"
                              htmlFor={`triggerSource.DIRECT`}
                            >
                              Сообщение в Директ
                            </label>
                            <Checkbox
                              id={`triggerSource.DIRECT`}
                              {...field}
                              defaultChecked={Boolean(field.value)}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                              }}
                            />
                          </div>
                        )}
                      />
                      <Controller
                        control={control}
                        name={`triggerSource.COMMENT`}
                        render={({ field }) => (
                          <div
                            className="flex items-center gap-2 border border-gray-200 rounded-md p-2 cursor-pointer"
                            style={{
                              borderColor: field.value
                                ? "var(--bs-primary)"
                                : "",
                            }}
                          >
                            <div className="pl-2">
                              <CommentIcon size={20} />
                            </div>
                            <label
                              className="text-lg font-bold flex-1"
                              htmlFor={`triggerSource.COMMENT`}
                            >
                              Комментарий к посту или Reels
                            </label>
                            <Checkbox
                              id={`triggerSource.COMMENT`}
                              {...field}
                              defaultChecked={Boolean(field.value)}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                              }}
                            />
                          </div>
                        )}
                      />
                    </div>
                    {/* Trigger type: Любое слово или кодовое слово */}
                    <div className="flex items-center gap-2 mb-4">
                      <FormControl>
                        <Controller
                          control={control}
                          name="triggerType"
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              defaultValue={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                              }}
                            >
                              <FormControlLabel
                                value="ANY"
                                control={<Radio />}
                                label="Любое слово"
                              />
                              <FormControlLabel
                                value="KEYWORD"
                                control={<Radio />}
                                label="Кодовое слово"
                              />
                              <div className="flex items-center">
                                <FormControlLabel
                                  value="INTENTION"
                                  control={<Radio />}
                                  label={
                                    <div className="flex items-center">
                                      <span className="mr-2">
                                        Распознавание сообщения
                                      </span>{" "}
                                      <AIIcon />
                                    </div>
                                  }
                                />
                              </div>
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    </div>
                    {watch("triggerType") === "KEYWORD" && (
                      <div className="gap-2">
                        <div className="text-lg font-bold mb-2">
                          Ключевые слова
                        </div>
                        <div className="flex flex-col gap-2">
                          <Controller
                            control={control}
                            name="keywords"
                            render={({ field }) => (
                              <Autocomplete
                                onChange={(e, value) => {
                                  field.onChange(value);
                                }}
                                value={field.value}
                                multiple
                                freeSolo
                                options={[]} // Можно оставить пустым, если подсказки не нужны
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Введите значения и нажмите Enter"
                                  />
                                )}
                              />
                            )}
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      {watch("triggerType") === "INTENTION" && (
                        <div className="text-lg font-bold mb-2">
                          <div className="text-lg font-bold mb-4">
                            Срабатывать если
                          </div>
                          <TextField
                            label="Введите намерение"
                            {...register("intention")}
                            fullWidth
                            helperText="Опишите намерения, начиная со слов Клиент хочет... или Клиент спрашивает о..."
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <div className="text-lg font-bold mb-2">
                        Запретить повторный запуск
                      </div>
                      <div className="text-sm text-gray-500">
                        Если автоматизация уже запущена, то она не будет
                        запущена повторно
                      </div>
                    </div>
                    <Switch {...register("preventDuplicateLaunch")} />
                  </div>
                  {/* <pre>{JSON.stringify(watch(), null, 2)}</pre> */}
                </section>
              )}
              {steps === 2 && (
                <section id="step-2">
                  <div className="text-lg font-bold mb-4">
                    Настройка сообщения
                  </div>
                  {getValues("nodes")?.map((node: any, index: number) => (
                    <div key={index} className="mb-8">
                      <div className="text-md font-bold mb-2 flex justify-between items-center">
                        {index === 0
                          ? "Приветственное сообщение"
                          : `Сообщение ${index + 1}`}
                        {index > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              console.log("Удаляем сообщение", index);
                              setValue(
                                "nodes",
                                getValues("nodes").filter(
                                  (node: any, i: number) => i !== index,
                                ),
                                {
                                  shouldTouch: true,
                                  shouldDirty: true,
                                  shouldValidate: true,
                                },
                              );
                            }}
                          >
                            <FaTrashAlt size={12} />
                          </IconButton>
                        )}
                      </div>
                      <div className="mb-2">
                        <TextField
                          label="Текст сообщения"
                          {...register(`nodes.${index}.text`, {
                            required: true,
                          })}
                          fullWidth
                          multiline
                          minRows={4}
                        />
                      </div>
                      <div>
                        {node.buttons?.map(
                          (button: any, buttonIndex: number) => (
                            <div key={buttonIndex} className="mb-2 flex gap-2 ">
                              <TextField
                                label="Текст кнопки"
                                {...register(
                                  `nodes.${index}.buttons.${buttonIndex}.text`,
                                  {},
                                )}
                                fullWidth
                              />
                              <TextField
                                label="Ссылка в кнопке"
                                {...register(
                                  `nodes.${index}.buttons.${buttonIndex}.url`,
                                )}
                                fullWidth
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setValue(
                                    `nodes.${index}.buttons`,
                                    node.buttons.filter(
                                      (button: any, i: number) =>
                                        i !== buttonIndex,
                                    ),
                                    {
                                      shouldTouch: true,
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                              >
                                <FaTrash size={12} />
                              </IconButton>
                            </div>
                          ),
                        )}
                        {/* <Button
                        variant="outlined"
                        type="button"
                        onClick={() => {
                          setValue(`nodes.${index}.buttons`, [
                            ...node.buttons,
                            { text: "", url: "" },
                          ]);
                        }}
                      >
                        Добавить кнопку
                      </Button> */}
                      </div>
                    </div>
                  ))}
                  {getValues("nodes").length < 5 && (
                    <Button
                      variant="outlined"
                      type="button"
                      onClick={() => {
                        setValue(
                          "nodes",
                          [
                            ...getValues("nodes"),
                            {
                              text: "Тестовое сообщение",
                              buttons: [{ text: "Текст кнопки", url: "" }],
                              image: null,
                              type: NodeType.MESSAGE,
                            },
                          ],
                          {
                            shouldTouch: true,
                            shouldDirty: true,
                            shouldValidate: true,
                          },
                        );
                      }}
                    >
                      Добавить сообщение
                    </Button>
                  )}
                  {/* <pre>{JSON.stringify(watch(), null, 2)}</pre> */}
                </section>
              )}
              {steps === 3 && (
                <section id="step-2">
                  <div className="text-lg font-bold mb-4">
                    Дополнительные настройки
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex flex-row gap-2">
                      <div className="flex items-center gap-2">
                        <AIIcon />
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-bold">Умный AI-ассистент</div>
                        <div className="text-sm text-gray-500">
                          Если в сценарии нет подходящего ответа, AI
                          автоматически ответит на вопрос клиента.
                        </div>
                      </div>
                    </div>
                    <div>
                      <Switch
                        {...register("aiEnabled")}
                        defaultChecked={watch("aiEnabled")}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div>Автоответы на комментарии</div>
                      <div className="text-sm text-gray-500">
                        Используем их в случайном порядке
                      </div>
                    </div>
                    <div>
                      <Switch
                        {...register("replyCommentsEnabled")}
                        defaultChecked={watch("replyCommentsEnabled")}
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    {watch("replyCommentsEnabled") &&
                      getValues("replyComments")?.map(
                        (replyComment: any, index: number) => (
                          <div key={index} className="mb-4">
                            <TextField
                              label="Текст автоответа"
                              {...register(`replyComments.${index}.text`, {
                                required: true,
                              })}
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const replyComments =
                                        getValues("replyComments");
                                      replyComments.splice(index, 1);
                                      setValue("replyComments", replyComments, {
                                        shouldTouch: true,
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      });
                                    }}
                                  >
                                    <FaTrash size={12} />
                                  </IconButton>
                                ),
                              }}
                            />
                          </div>
                        ),
                      )}
                    {watch("replyCommentsEnabled") &&
                      getValues("replyComments").length < 3 && (
                        <Button
                          variant="outlined"
                          type="button"
                          onClick={() => {
                            setValue(
                              "replyComments",
                              [...getValues("replyComments"), { text: "" }],
                              {
                                shouldTouch: true,
                                shouldDirty: true,
                                shouldValidate: true,
                              },
                            );
                          }}
                        >
                          <FaPlus size={12} />
                          Добавить автоответ
                        </Button>
                      )}
                  </div>
                  {/* <pre>{JSON.stringify(watch(), null, 2)}</pre> */}
                </section>
              )}
            </div>
            <div className="automation-create-keyword-footer sticky bottom-0 border-t bg-white px-4 sm:px-8 py-4 flex justify-end gap-2">
              {isOnboarding && steps < 3 && (
                <Button variant="contained" type="submit" disabled={!isValid}>
                  Создать чат-бота
                </Button>
              )}
              {steps < 3 ? (
                <Button
                  variant={isOnboarding ? "outlined" : "contained"}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("Go next step");
                    setSteps((prev) => prev + 1);
                  }}
                  disabled={!isValid}
                >
                  Далее
                </Button>
              ) : (
                <Button variant="contained" type="submit" disabled={!isValid}>
                  {automation ? "Обновить чат-бота" : "Создать чат-бота"}
                </Button>
              )}
            </div>
          </form>
          <ChatbotTestDialog
            open={!!testDialog}
            username={testDialog?.username}
            keywords={testDialog?.keywords}
            direct={testDialog?.direct}
            comment={testDialog?.comment}
            onClose={() => {
              setTestDialog(null);
              navigate(isOnboarding ? "/admin/stats" : "/admin/automations");
            }}
          />
        </div>

        {/* Превью */}
        <div className="automation-create-keyword-right hidden lg:flex flex-1 h-full flex-col items-center justify-center gap-4 overflow-y-auto py-6 bg-[radial-gradient(120%_90%_at_50%_30%,#f6f0fb_0%,#eef0f7_60%,#e9ecf3_100%)]">
          <div className="text-sm text-gray-500">
            Так клиент увидит ответ бота
          </div>
          <div>
            {tab === "messages" && watch("triggerSource.DIRECT") && (
              <IPhoneChat account={account} automation={watch()} />
            )}
            {tab === "comments" && watch("triggerSource.COMMENT") && (
              <PostWithComments account={account} automation={watch()} />
            )}
          </div>
          <div className="flex justify-center">
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              variant="fullWidth"
            >
              {watch("triggerSource.DIRECT") && (
                <Tab
                  label="Сообщения"
                  value="messages"
                  sx={{
                    fontSize: "0.7rem",
                    height: "40px",
                  }}
                />
              )}
              {watch("triggerSource.COMMENT") && (
                <Tab
                  label="Комментарии"
                  value="comments"
                  sx={{
                    fontSize: "0.7rem",
                    height: "40px",
                  }}
                />
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};
export default withRouter(
  withRemoteDataAndSpinner(AutomationCreateKeyword, (router: any) => {
    let requests: any = {
      accounts: () => channelConnections.getAll({ platform: "INSTAGRAM" }),
    };
    if (router.params.automationID) {
      // Бэкенд отдаёт { automation, stats } — форме нужна сама автоматизация
      requests["automation"] = () =>
        automationsService
          .getAutomation(Number(router.params.automationID))
          .then((res) => ({ data: res.data?.automation }));
    }
    return useLoadAll(requests);
  }),
);
